import type { PlanInput, PlanType, Reason, RecommendationDay, Recommendations, Suggestion } from '@painpal/contracts';
import { weekdayOf } from '@painpal/core';
import { formatHoursMinutes, formatSigned, formatWeekdayDayMonth } from '../../lib/format';
import { plural } from '../../lib/plural';
import { formatEstimate, PLAN_LABELS } from '../calendar/planModel';

// Sentence case for the + label; a word with a digit (VO2) is an acronym and keeps its caps.
function title(type: PlanType): string {
  const words = PLAN_LABELS[type].split(' ').map((word) => (/\d/.test(word) ? word : word.toLowerCase()));
  const text = words.join(' ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

export function reasonText(reason: Reason): string {
  const n = reason.value ?? 0;
  switch (reason.code) {
    case 'form': return `FORM ${formatSigned(n)}`;
    case 'since-hard': return `${n} ${plural(n, 'DAY')} SINCE A HARD DAY`;
    case 'hard-in-a-row': return `${n} HARD DAYS IN A ROW`;
    case 'day-after-hard': return 'DAY AFTER A HARD DAY';
    case 'hard-this-week': return `${n} HARD ${plural(n, 'DAY')} THIS WEEK`;
    case 'ramp': return `FITNESS ${formatSigned(n)} THIS WEEK`;
    case 'zone-mix-low': return 'LITTLE Z4 IN 3 WEEKS';
    case 'zone-mix-high': return 'PLENTY OF Z4 LATELY';
    case 'usual-day': return `YOUR USUAL ${reason.weekday === undefined ? 'DAY' : reason.weekday.toUpperCase()}`;
    case 'usual-off': return 'YOUR USUAL DAY OFF';
  }
}

export const whyLine = (reasons: readonly Reason[]): string => reasons.map(reasonText).join(' · ');

export function suggestionInput(s: Suggestion): PlanInput {
  return { type: s.type, durationS: s.durationS, note: s.structure === '' ? null : s.structure };
}

export const suggestionDuration = (s: Suggestion): string => (s.durationS === null ? '' : formatHoursMinutes(s.durationS));
const spaced = (text: string): string => (text === '' ? '' : ` ${text}`);

// "Threshold 1:00", or "Rest" with no duration.
export const suggestionName = (s: Suggestion): string => `${title(s.type)}${spaced(suggestionDuration(s))}`;

export type Outcome = { kind: 'added' | 'swapped'; suggestion: Suggestion } | { kind: 'hidden' | 'kept' };

// What Today's plan announces after each action.
export function outcomeText(outcome: Outcome): string {
  switch (outcome.kind) {
    case 'added': return `${suggestionName(outcome.suggestion)} added to today's plan`;
    case 'swapped': return `Today's plan swapped to ${suggestionName(outcome.suggestion)}`;
    case 'hidden': return 'Suggestion hidden for today';
    case 'kept': return "Swap hidden, today's plan kept";
  }
}

export function addLabel(s: Suggestion, date: string): string {
  const weekday = weekdayOf(date);
  return `Add suggested ${suggestionName(s)} on ${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}`;
}

export function suggestionSummary(s: Suggestion): string {
  const structure = s.structure === '' ? '' : ` · ${s.structure}`;
  return `${PLAN_LABELS[s.type]}${spaced(suggestionDuration(s))}${structure} · ${formatEstimate(s.estimatedLoad)} LOAD`;
}

export const suggestedRowLabel = (date: string, s: Suggestion): string => `${formatWeekdayDayMonth(date)} · SUGGESTED ${suggestionSummary(s)}`;

export interface ActiveRecommendation extends RecommendationDay { basis: string }
export type IsDismissed = (date: string, basis: string) => boolean;

// The day's suggestion or swap note, unless it was dismissed against the same basis.
export function recommendationOn(data: Recommendations | undefined, date: string, isDismissed: IsDismissed): ActiveRecommendation | null {
  const day = data?.days.find((d) => d.date === date);
  if (data === undefined || day === undefined || isDismissed(date, data.basis)) return null;
  return { ...day, basis: data.basis };
}

export const dismissalKey = (date: string, basis: string): string => `${date}@${basis}`;
// A dismissal of a past day can't hide anything any more.
export const keepCurrent = (keys: readonly string[], today: string): string[] => keys.filter((key) => key.slice(0, 10) >= today);
